/**
 * Redeem attempts card on the account-detail page (OCP-514).
 *
 * Read-only. Self-contained: loads its own
 * `GET /v1/admin/accounts/:id/redeem-attempts`. Shows every invite/redeem
 * attempt this account made - success AND every failure reason - so a failed
 * redemption is no longer invisible (the operator can tell a wrong-code
 * attempt from "never tried"). Shows the FULL entered code (this is an
 * admin-only surface), the outcome, the matched code when the entered string
 * resolved to a real invite code, country, IP, and user agent.
 *
 * A load failure surfaces an inline error rather than silently hiding.
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { RedeemOutcomePill } from './RedeemOutcomePill';
import { fmtDateTime } from '../lib/format-time';
import { GeoIpAttribution } from './GeoIpAttribution';

interface RedeemAttemptRow {
  id: string;
  account_id: string | null;
  user_email: string;
  entered_code: string;
  matched_code_id: string | null;
  matched_code: string | null;
  outcome: string;
  ip_address: string | null;
  country: string | null;
  user_agent: string | null;
  created_at: string;
}

function fmtUtc(iso: string): string {
  return fmtDateTime(iso);
}

export function RedeemAttemptsCard({ customerId }: { customerId: string }) {
  const [rows, setRows] = useState<RedeemAttemptRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api<{ attempts: RedeemAttemptRow[] }>(
        `/v1/admin/accounts/${customerId}/redeem-attempts`,
      );
      setRows(r.attempts);
    } catch (err) {
      setError(err instanceof ApiError ? `Load failed (HTTP ${err.status}).` : 'Load failed.');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Redeem attempts</h2>
        {rows && rows.length > 0 && (
          <span className="text-xs text-gray-500">
            {rows.length} {rows.length === 1 ? 'attempt' : 'attempts'}
          </span>
        )}
      </div>

      {error && <div className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</div>}

      {loading && !rows && <div className="mt-3 text-sm text-gray-500">Loading…</div>}

      {rows &&
        (rows.length === 0 ? (
          <div className="mt-3 rounded border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 px-3 py-4 text-sm text-gray-500">
            No redeem attempts recorded for this account.
          </div>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[820px] text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2">Time</th>
                  <th className="px-3 py-2">Entered code</th>
                  <th className="px-3 py-2">Outcome</th>
                  <th className="px-3 py-2">Matched code</th>
                  <th className="px-3 py-2">Country</th>
                  <th className="px-3 py-2">IP</th>
                  <th className="px-3 py-2">User agent</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {rows.map((a) => (
                  <tr key={a.id}>
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-500">
                      {fmtUtc(a.created_at)}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{a.entered_code}</td>
                    <td className="px-3 py-2 text-xs">
                      <RedeemOutcomePill outcome={a.outcome} />
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-500">
                      {a.matched_code ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-xs">{a.country ?? '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-500">
                      {a.ip_address ?? '—'}
                    </td>
                    <td
                      className="max-w-[220px] truncate px-3 py-2 text-xs text-gray-500"
                      title={a.user_agent ?? undefined}
                    >
                      {a.user_agent ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <GeoIpAttribution />
          </div>
        ))}
    </div>
  );
}
